import { action, computed, observable, configure, makeObservable } from 'mobx';
import { orderByDistance } from 'geolib';

import { IAccess } from '../models/access';
import { ILine } from '../models/line';
import { IPlace } from '../models/place';
import { ITargetPlace } from '../models/targetPlace';

import origins from '../fixedData/origins';

import { getLocale } from '../../utils/pathHelper';
import locales, { ITranslation } from '../locales';

configure({ isolateGlobalState: true });

export default class UiStore {
  @observable public access: IAccess = { path: [], duration: null };
  @observable public errors: string[] = [];
  @observable public isLoading: boolean = false;
  @observable public mapCenter: { lat: number, lng: number } = { lat: 35.6811673, lng: 139.7670516 };
  @observable public origins: IPlace[] = origins;
  @observable public originIndex: number = 0;
  @observable public placeOpenIndex: number = 0;
  @observable public targetPlace: ITargetPlace;

  // 翻訳
  @observable public t: ITranslation = locales[getLocale() || 'en'];

  constructor() {
    makeObservable(this);
  }

  @computed get isAccessAvailable(): boolean {
    const { access } = this;
    return !!access && !!access.path && access.path.length > 0;
  }

  @computed get durationInHour(): number {
    if (!this.isAccessAvailable) { return null; }
    const { duration } = this.access;
    return duration ? Number((duration / 60).toFixed(1)) : null;
  }

  @computed get currentOrigin(): IPlace {
    return this.origins[this.originIndex];
  }

  @computed get currentPlace(): IPlace {
    return this.places[this.placeOpenIndex];
  }

  @computed get places(): IPlace[] {
    const result = [];
    if (this.isAccessAvailable) {
      const { access } = this;
      access.path.map((line: ILine, i: number) => {
        if (i === access.path.length - 1) {
          result.push(line.origin);
          result.push(line.dest);
        } else {
          result.push(line.origin);
        }
      });
    }
    return result;
  }

  @action.bound
  public updateAccess({ origin_id, dest_id }: { origin_id: string, dest_id: string }): void {
    const defaultAccess = { path: [], duration: null };
    this.access = defaultAccess;
    this.errors = [];
    this.isLoading = true;
    fetch(`/snow/api/v1/accesses/by_duration_and_interchange/${origin_id},${dest_id}`)
      .then((data) => {
        return data.json();
      })
      .then(({ accesses, error }: { accesses: Array<{ path: ILine[], duration: number }>, error?: string|string[] }) => {
        if (error) { console.error('AccessUI', error); }
        this.access = accesses[0];
        this.errors = [];
        this.isLoading = false;
        if (!this.isAccessAvailable) {
          this.errors.push(this.t.accessNotFound());
        }
      })
      .catch((error) => {
        console.error(error);
        this.access = defaultAccess;
        this.errors.push(error);
        this.isLoading = false;
      });
  }

  @action.bound
  public updateOrign(e: any) {
    this.originIndex = Number(e.target.value);
    this.updateAccess({ origin_id: this.currentOrigin.place_id, dest_id: this.targetPlace.place_id });
  }

  @action.bound
  public orderOrigins(target: { latitude: number, longitude: number }, callback: () => void = null): void {
    const sorted = orderByDistance(target, origins);
    const newOrigins = sorted.map((item: { distance: number, key: string }) => {
      return this.origins[Number(item.key)];
    });

    const favoredOrigin = document.querySelectorAll('.access-ui')[0].getAttribute('data-favored-origin')
    if (favoredOrigin) {
      newOrigins.sort(function(x,y){ return x.place_id == favoredOrigin ? -1 : y.place_id == favoredOrigin ? 1 : 0; });
    }
    this.origins = newOrigins;

    if (callback != null) {
      callback();
    }
  }
}
